import { useCallback, useEffect, useRef } from 'react';

import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import { InviteSegmentEvents } from '../constants';
import type { CloseReason, InviteFlowMilestoneKey } from '../RenterInvite.types';
import { useInviteTracking } from './useInviteTracking';

const createEmptyMilestones = (): Record<InviteFlowMilestoneKey, boolean> => ({
  hadFlowStarted: false,
  hadPropertySelected: false,
  hadLabelSelected: false,
  hadSubmitAttempt: false,
  hadSubmitSuccess: false,
});

export const useInviteDrawerSessionTracking = () => {
  const { isOpen, flowId } = useRenterInviteDrawerStore();
  const { trackInvite, entryPoint } = useInviteTracking();

  const milestonesRef = useRef<Record<InviteFlowMilestoneKey, boolean>>(createEmptyMilestones());
  const prevOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      trackInvite(InviteSegmentEvents.DrawerOpened, { entry_point: entryPoint });
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, trackInvite, entryPoint]);

  useEffect(() => {
    if (flowId) {
      milestonesRef.current = createEmptyMilestones();
    }
  }, [flowId]);

  const recordInviteFlowMilestone = useCallback((key: InviteFlowMilestoneKey) => {
    milestonesRef.current[key] = true;
  }, []);

  // Must fire exactly once per session; caller owns the guard (see RenterInvite.drawer.tsx).
  const trackDrawerClosed = useCallback(
    (reason: CloseReason) => {
      trackInvite(InviteSegmentEvents.DrawerClosed, {
        close_reason: reason,
        had_flow_started: milestonesRef.current.hadFlowStarted,
        had_property_selected: milestonesRef.current.hadPropertySelected,
        had_label_selected: milestonesRef.current.hadLabelSelected,
        had_submit_attempt: milestonesRef.current.hadSubmitAttempt,
        had_submit_success: milestonesRef.current.hadSubmitSuccess,
      });
    },
    [trackInvite],
  );

  return { recordInviteFlowMilestone, trackDrawerClosed };
};
